import {
  type ActionButtonConfig,
  BDDialog,
  type DialogResult,
} from '@brightdrop/bd-ui';
import { translateText } from '@gm-commercial/translations';
import { Button, Typography } from '@mui/material';
import {
  type ComponentType,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import TableFilterIcon from '../../../assets/icons/tableFilterIcon.svg?react';
import FilterPopover from '../FilterPopover/FilterPopover';
import type { PopoverComponentProps } from '../FilterPopover/FilterPopover.model';
import {
  TableFilterDisplayMode,
  type TableFilterProps,
} from './TableFilter.model';
import useStyles from './TableFilter.styles';
import {
  countArrayAttributeValues,
  filterByPermanentKeys,
} from './TableFilter.utils';

const TableFilter = <T,>({
  saveFilterOptions,
  defaultState,
  entity,
  onModalOpenUpdate,
  modalOpen,
  permanentFilters,
  id,
  displayMode = TableFilterDisplayMode.MODAL,
  renderCustomButton,
  actionButtonsConfigOverride,
  extendedMobile = false,
  customPopoverMaxHeight,
  useSpaceBetweenButtons,
  stableAnchor,
  ...props
}: TableFilterProps<T>): JSX.Element => {
  const { classes } = useStyles();

  const [filterOptions, setFilterOptions] = useState<T | undefined>(entity);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const restoreFocusAnchorRef = useRef<HTMLElement | null>(null);

  const filteredFilterCriteria = useMemo(
    () =>
      filterByPermanentKeys(
        entity as unknown as object,
        permanentFilters as []
      ),
    [entity, permanentFilters]
  );
  const filterCount = useMemo(
    () =>
      countArrayAttributeValues(filteredFilterCriteria as unknown as object),
    [filteredFilterCriteria]
  );

  const closeFilterModal = useCallback(() => {
    if (displayMode === TableFilterDisplayMode.POPOVER) {
      if (stableAnchor && anchorEl) {
        restoreFocusAnchorRef.current = anchorEl;
      }
      setAnchorEl(null);
    }
    onModalOpenUpdate(false);
  }, [anchorEl, displayMode, onModalOpenUpdate, stableAnchor]);

  useEffect(() => {
    if (modalOpen || !stableAnchor) {
      return;
    }

    restoreFocusAnchorRef.current?.focus();
    restoreFocusAnchorRef.current = null;
  }, [modalOpen, stableAnchor]);

  const openFilterModal = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (
        stableAnchor &&
        displayMode === TableFilterDisplayMode.POPOVER &&
        modalOpen
      ) {
        closeFilterModal();
        return;
      }

      if (displayMode === TableFilterDisplayMode.POPOVER) {
        setAnchorEl(event.currentTarget);
      }
      setFilterOptions(entity);
      onModalOpenUpdate(true);
    },
    [
      closeFilterModal,
      displayMode,
      entity,
      modalOpen,
      onModalOpenUpdate,
      stableAnchor,
    ]
  );

  const handleResult = useCallback((result: DialogResult<T>) => {
    // A dialog may report a status with no entity; that carries no filter
    // selection, so the in-progress one is kept rather than cleared.
    if (result.entity === undefined) {
      return;
    }
    setFilterOptions(result.entity);
  }, []);

  const submitButton = useCallback(() => {
    if (filterOptions) {
      saveFilterOptions(filterOptions);
    }
    closeFilterModal();
  }, [closeFilterModal, filterOptions, saveFilterOptions]);

  const resetFilterOptions = useCallback(() => {
    setFilterOptions(defaultState);
  }, [defaultState]);

  const buttonsConfig = useMemo(
    () => [
      {
        displayLabel: translateText('common:muiTable.filter.reset'),
        accessibleLabel: translateText('common:muiTable.filter.reset'),
        variant: 'secondary',
        size: 'medium',
        onClick: resetFilterOptions,
        id: 'filter-modal-reset-button',
        ...(actionButtonsConfigOverride?.[0] || {}),
      } as ActionButtonConfig,
      {
        displayLabel: translateText('common:muiTable.filter.apply'),
        accessibleLabel: translateText('common:muiTable.filter.apply'),
        variant: 'primary',
        size: 'medium',
        onClick: submitButton,
        id: 'filter-modal-submit-button',
        ...(actionButtonsConfigOverride?.[1] || {}),
      } as ActionButtonConfig,
    ],
    [actionButtonsConfigOverride, resetFilterOptions, submitButton]
  );

  useEffect(() => {
    setFilterOptions(entity);
  }, [entity]);

  return (
    <>
      {renderCustomButton ? (
        renderCustomButton(openFilterModal, filterCount)
      ) : (
        <Button
          className={`${classes.button} ${anchorEl ? 'active' : ''}`}
          onClick={openFilterModal}
          startIcon={<TableFilterIcon />}
          data-testid={`${id}-filter-button`}
        >
          {translateText('common:muiTable.filter.title')}
          {!!filterCount && (
            <Typography
              className={classes.countBadge}
              data-testid="count"
              variant="caption_medium"
            >
              {filterCount}
            </Typography>
          )}
        </Button>
      )}
      {displayMode === TableFilterDisplayMode.MODAL ? (
        <BDDialog<T>
          {...props}
          openModal={!!modalOpen}
          onCloseModal={onModalOpenUpdate}
          buttonsConfig={buttonsConfig}
          onResult={handleResult}
          entity={filterOptions}
          extendedMobileView={extendedMobile}
        />
      ) : (
        <FilterPopover<T>
          {...props}
          buttonsConfig={buttonsConfig}
          openPopover={!!modalOpen}
          onClosePopover={closeFilterModal}
          onResult={handleResult}
          entity={filterOptions}
          anchorEl={anchorEl}
          component={props.component as ComponentType<PopoverComponentProps<T>>}
          customMaxHeight={customPopoverMaxHeight}
          useSpaceBetweenButtons={useSpaceBetweenButtons}
          stableAnchor={stableAnchor}
        />
      )}
    </>
  );
};

export default TableFilter;
